import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Factory, Plus, Trash, ArrowDown, ArrowRight, Package } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useMonth } from "../contexts/MonthContext";

const f2 = (v) => (v == null ? "-" : (+v).toFixed(2));
const ybg = (v) => (v && +v > 0 ? "bg-[#FEF9C3]" : "");

export default function JobWork() {
  const [log, setLog] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [inwardOpen, setInwardOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [inwards, setInwards] = useState([]);
  const [issues, setIssues] = useState([]);
  const { period } = useMonth();

  const fetchAll = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([
        axios.get(`${API}/job-work/log-all`),
        axios.get(`${API}/processes`),
      ]);
      setLog(l.data || []);
      setProcesses(p.data || []);
    } catch { toast.error("Failed to load job-work data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll, period.month, period.year]);

  const openDetail = async (row) => {
    setSelected(row);
    try {
      const [inw, iss] = await Promise.all([
        axios.get(`${API}/job-work/inward`, { params: { part_id: row.part_id } }),
        axios.get(`${API}/job-work/issue`, { params: { part_id: row.part_id } }),
      ]);
      setInwards(inw.data || []);
      setIssues(iss.data || []);
    } catch { toast.error("Failed to load history"); }
  };

  const procName = (code) => (processes.find(p => p.code === code)?.name) || code;

  return (
    <Layout>
      <div className="p-6 space-y-5" data-testid="job-work-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Job Work</h1>
            <p className="text-sm text-[#64748B] mt-1">Customer-supplied material: inward, issue to ops, gate stock.</p>
          </div>
        </div>

        {/* Summary tiles — period-scoped */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Opening" value={f2(log.reduce((s, r) => s + (r.opening_balance || 0), 0))} color="#1D2951" />
          <Tile label="Received" value={f2(log.reduce((s, r) => s + (r.received || 0), 0))} color="#3B82F6" />
          <Tile label="Issued" value={f2(log.reduce((s, r) => s + (r.issued || 0), 0))} color="#7C3AED" />
          <Tile label="Customer Stock" value={f2(log.reduce((s, r) => s + (r.customer_stock || 0), 0))} color="#059669"
            hint="Opening + Received − Dispatch" />
          <Tile label="Machining Stock" value={f2(log.reduce((s, r) => s + (r.machining_stock || 0), 0))} color="#DC2626"
            hint="Opening + Issued − Received" />
        </div>

        <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="job-work-log">
          <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
            <Factory size={18} className="text-[#F5DF4D]" />
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Job Work Log</h2>
            <span className="ml-auto bg-[#F5DF4D] text-[#1D2951] text-xs font-bold rounded-full px-2 py-0.5">{log.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1D2951] text-white">
                  <th className="px-3 py-2 text-left text-xs font-bold">Part No</th>
                  <th className="px-3 py-2 text-left text-xs font-bold">Customer</th>
                  <th className="px-3 py-2 text-right text-xs font-bold">Opening</th>
                  <th className="px-3 py-2 text-right text-xs font-bold">Received</th>
                  <th className="px-3 py-2 text-right text-xs font-bold">Issued</th>
                  <th className="px-3 py-2 text-right text-xs font-bold">Dispatch</th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-[#86EFAC]" title="Opening + Received − Dispatch">Cust. Stock</th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-[#FCA5A5]" title="Opening + Issued − Received">Machining Stock</th>
                  <th className="px-3 py-2 text-left text-xs font-bold">Issued To (Ops)</th>
                  <th className="px-3 py-2 text-center text-xs font-bold w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" className="text-center py-8 text-[#64748B]">Loading…</td></tr>
                ) : log.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-10 text-[#64748B]">
                    No job-work parts yet. Mark a part as "Job Work" in Item Master.
                  </td></tr>
                ) : log.map((r, i) => (
                  <tr key={r.part_id} className={`border-b border-[#E2E8F0] hover:bg-[#F8FAFC] ${i % 2 ? 'bg-[#FAFBFC]' : ''}`} data-testid={`jw-row-${r.part_no}`}>
                    <td className="px-3 py-2.5 font-bold text-[#1D2951]">{r.part_no}</td>
                    <td className="px-3 py-2.5 text-[#475569]">{r.customer}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.opening_balance)}`}>{f2(r.opening_balance)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.received)}`}>{f2(r.received)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.issued)}`}>{f2(r.issued)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.dispatched)}`}>{f2(r.dispatched)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#059669] ${ybg(r.customer_stock)}`}>{f2(r.customer_stock)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#DC2626] ${ybg(r.machining_stock)}`}>{f2(r.machining_stock)}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {Object.entries(r.issued_by_operation || {}).map(([op, qty]) => (
                        <span key={op} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-[#EDE9FE] text-[#5B21B6] rounded text-[11px] font-mono">
                          {procName(op)}: {f2(qty)}
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 justify-center">
                        <Button size="sm" variant="outline" onClick={() => { setSelected(r); setInwardOpen(true); }}
                          className="h-7 px-2 text-[10px] font-bold" data-testid={`jw-inward-${r.part_no}`} title="Record inward">
                          <ArrowDown size={10} className="mr-1" /> IN
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelected(r); setIssueOpen(true); }}
                          className="h-7 px-2 text-[10px] font-bold text-[#7C3AED] border-[#7C3AED]" data-testid={`jw-issue-${r.part_no}`} title="Issue to op">
                          <ArrowRight size={10} className="mr-1" /> ISS
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openDetail(r)}
                          className="h-7 px-2 text-[10px]" title="History">
                          <Package size={10} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* History */}
        {selected && !inwardOpen && !issueOpen && (
          <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="jw-history">
            <div className="bg-[#F1F5F9] px-5 py-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#1D2951]">History — {selected.part_no}</h3>
              <Button size="sm" variant="outline" onClick={() => setSelected(null)} className="h-7 text-xs">Close</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-4 border-r border-[#E2E8F0]">
                <h4 className="text-xs uppercase font-bold text-[#059669] mb-2">Inwards ({inwards.length})</h4>
                <table className="w-full text-xs">
                  <thead><tr className="text-[#64748B]"><th className="text-left py-1">Date</th><th className="text-right py-1">Quantity</th><th className="text-left py-1">Invoice</th><th></th></tr></thead>
                  <tbody>
                    {inwards.map(r => (
                      <tr key={r.id} className="border-t border-[#E2E8F0]">
                        <td className="py-1">{r.date}</td>
                        <td className="py-1 text-right font-mono">{f2(r.received_qty)}</td>
                        <td className="py-1 text-[#475569]">{r.invoice_no || '-'}</td>
                        <td className="text-right">
                          <button onClick={async () => { if (window.confirm('Delete?')) { await axios.delete(`${API}/job-work/inward/${r.id}`); openDetail(selected); fetchAll(); }}}
                            className="text-[#DC2626] hover:underline">x</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4">
                <h4 className="text-xs uppercase font-bold text-[#7C3AED] mb-2">Issues ({issues.length})</h4>
                <table className="w-full text-xs">
                  <thead><tr className="text-[#64748B]"><th className="text-left py-1">Date</th><th className="text-right py-1">Quantity</th><th className="text-left py-1">Issued To</th><th></th></tr></thead>
                  <tbody>
                    {issues.map(r => (
                      <tr key={r.id} className="border-t border-[#E2E8F0]">
                        <td className="py-1">{r.date}</td>
                        <td className="py-1 text-right font-mono">{f2(r.issue_qty)}</td>
                        <td className="py-1 text-[#475569]">{procName(r.issued_to)}</td>
                        <td className="text-right">
                          <button onClick={async () => { if (window.confirm('Delete?')) { await axios.delete(`${API}/job-work/issue/${r.id}`); openDetail(selected); fetchAll(); }}}
                            className="text-[#DC2626] hover:underline">x</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        )}

        {/* Dialogs */}
        <InwardDialog open={inwardOpen} onClose={() => { setInwardOpen(false); fetchAll(); }} part={selected} />
        <IssueDialog open={issueOpen} onClose={() => { setIssueOpen(false); fetchAll(); }} part={selected} processes={processes} />
      </div>
    </Layout>
  );
}

function Tile({ label, value, color, hint }) {
  return (
    <Card className="p-4 border border-[#E2E8F0] text-center" title={hint || ""}>
      <p className="text-xs text-[#64748B] uppercase font-bold">{label}</p>
      <p className="text-2xl font-black font-mono mt-1" style={{ color }}>{value}</p>
      {hint && <p className="text-[10px] text-[#94A3B8] mt-1 italic">{hint}</p>}
    </Card>
  );
}

function InwardDialog({ open, onClose, part }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, received_qty: "", invoice_no: "", notes: "" });
  useEffect(() => { if (open) setForm({ date: today, received_qty: "", invoice_no: "", notes: "" }); }, [open, today]);
  const submit = async () => {
    if (!(+form.received_qty > 0)) return toast.error("Quantity must be > 0");
    try {
      await axios.post(`${API}/job-work/inward`, {
        part_id: part.part_id, part_no: part.part_no,
        date: form.date, received_qty: +form.received_qty,
        invoice_no: form.invoice_no, notes: form.notes,
      });
      toast.success("Inward recorded");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!part) return null;
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="inward-dialog">
        <DialogHeader><DialogTitle>Record Inward — {part.part_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[11px] font-semibold">Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="min-h-[40px]" data-testid="inward-date" /></div>
          <div><Label className="text-[11px] font-semibold">Received Quantity</Label>
            <Input type="number" value={form.received_qty} onChange={e => setForm({ ...form, received_qty: e.target.value })} className="min-h-[40px]" data-testid="inward-qty" autoFocus /></div>
          <div><Label className="text-[11px] font-semibold">Invoice No</Label>
            <Input value={form.invoice_no} onChange={e => setForm({ ...form, invoice_no: e.target.value })} className="min-h-[40px]" data-testid="inward-invoice" /></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1 min-h-[40px]">Cancel</Button>
            <Button onClick={submit} className="flex-1 min-h-[40px] bg-[#059669] hover:bg-[#047857] text-white" data-testid="inward-submit">
              <Plus size={14} className="mr-1" /> Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ open, onClose, part, processes }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, issue_qty: "", issued_to: "", notes: "" });
  useEffect(() => {
    if (open) setForm({ date: today, issue_qty: "", issued_to: (part?.operations?.[0] || ""), notes: "" });
  }, [open, part, today]);
  const submit = async () => {
    if (!(+form.issue_qty > 0)) return toast.error("Quantity must be > 0");
    if (!form.issued_to) return toast.error("Select destination op");
    try {
      await axios.post(`${API}/job-work/issue`, {
        part_id: part.part_id, part_no: part.part_no,
        date: form.date, issue_qty: +form.issue_qty,
        issued_to: form.issued_to, notes: form.notes,
      });
      toast.success("Issued");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!part) return null;
  const allowedOps = part.operations && part.operations.length ? part.operations : (processes || []).map(p => p.code);
  const procName = (c) => (processes || []).find(p => p.code === c)?.name || c;
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="issue-dialog">
        <DialogHeader><DialogTitle>Issue to Operation — {part.part_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="p-2 bg-[#F1F5F9] rounded text-xs text-[#475569]">Gate stock: <b className="text-[#059669]">{f2(part.gate_stock)}</b></div>
          <div><Label className="text-[11px] font-semibold">Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="min-h-[40px]" data-testid="issue-date" /></div>
          <div><Label className="text-[11px] font-semibold">Issue Quantity</Label>
            <Input type="number" value={form.issue_qty} onChange={e => setForm({ ...form, issue_qty: e.target.value })} className="min-h-[40px]" data-testid="issue-qty" autoFocus /></div>
          <div><Label className="text-[11px] font-semibold">Issued To</Label>
            <Select value={form.issued_to} onValueChange={v => setForm({ ...form, issued_to: v })}>
              <SelectTrigger className="min-h-[40px]" data-testid="issue-to"><SelectValue placeholder="Select operation" /></SelectTrigger>
              <SelectContent>
                {allowedOps.map(op => <SelectItem key={op} value={op}>{procName(op)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1 min-h-[40px]">Cancel</Button>
            <Button onClick={submit} className="flex-1 min-h-[40px] bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="issue-submit">
              <ArrowRight size={14} className="mr-1" /> Issue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
